import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { OpenModalHookType } from '@hooks/useOpenModal';
import noImage from '@assets/img/noImage.jpeg';
import store from '@store/index';
import { setAlert } from '@store/appStore';
import { Dish, DishModifierGroup, getDishGroupKey, getDishGroupLabel } from '@api/Dishes/Dish';
import { Recipe } from '@api/Recipes/Recipe';
import { Input } from '@api/Inputs/Input';
import { ModifierGroup, ModifierGroupOption } from '@api/ModifierGroups/ModifierGroup';
import { OrderLineRemovedIngredient } from '@api/Orders/OrderLine';
import {
    buildOrderLineV2,
    buildSelectionsFromModifiers,
    DishSelections,
    getGroupDistinctCount,
    getGroupEffectiveMax,
    getGroupMinRequired,
    getGroupSelectedTotal,
    validateDishSelections
} from '@api/Orders/OrderLineBuilder';
import { ShoppingCartLineV2 } from '@api/ShoppingCart/ShoppingCartV2';
import { currencyFormatter } from '@utils/Numbers';

interface ModalDishInfoProps {
    modalDishInfo: OpenModalHookType;
    dish?: Dish; // producto elegido del grid (o el resuelto al editar una línea)
    editingLine?: ShoppingCartLineV2; // línea del carrito en edición
    modifierGroupsById: Map<string, ModifierGroup>;
    recipesById: Map<string, Recipe>;
    inputsById: Map<string, Input>;
    // Agotados por sucursal (§4.3): se marcan, NO bloquean la venta
    unavailableRecipeIds: Set<string>;
    onAddLine: (line: ShoppingCartLineV2) => void;
}

// La aritmética de la línea y la validación viven en @api/Orders/OrderLineBuilder (puras y testeadas)
const cloneSelections = (selections: DishSelections): DishSelections => {
    const clone: DishSelections = new Map();
    selections.forEach((options, groupId) => clone.set(groupId, new Map(options)));
    return clone;
};

export const ModalDishInfo: React.FunctionComponent<ModalDishInfoProps> = (props) => {
    const {
        modalDishInfo,
        dish,
        editingLine,
        modifierGroupsById,
        recipesById,
        inputsById,
        unavailableRecipeIds,
        onAddLine
    } = props;

    const [quantity, setQuantity] = useState<number>(1);
    const [takeAway, setTakeAway] = useState<boolean>(true);
    const [note, setNote] = useState<string>('');
    const [selections, setSelections] = useState<DishSelections>(new Map());
    const [removedInputIds, setRemovedInputIds] = useState<Set<string>>(new Set());

    // Ingredientes que el cliente puede quitar: items `optional` de las recetas componentes
    const removableIngredients = useMemo(() => {
        const ingredients: { inputId: string; name: string }[] = [];
        const seen = new Set<string>();
        (dish?.components || []).forEach((component) => {
            const recipe = recipesById.get(component.recipeId);
            (recipe?.items || []).forEach((item) => {
                if (item.optional && !seen.has(item.inputId)) {
                    seen.add(item.inputId);
                    ingredients.push({ inputId: item.inputId, name: inputsById.get(item.inputId)?.name || item.inputId });
                }
            });
        });
        return ingredients;
    }, [dish, recipesById, inputsById]);

    const resetData = () => {
        setQuantity(1);
        setTakeAway(true);
        setNote('');
        setSelections(new Map());
        setRemovedInputIds(new Set());
    };

    const handleClose = () => {
        resetData();
        modalDishInfo.toggle();
    };

    useEffect(() => {
        if (!modalDishInfo.modalOpen) return;
        if (editingLine) {
            // Modo edición: rehidratar la línea del carrito
            setQuantity(editingLine.quantity);
            setTakeAway(editingLine.takeAway);
            setNote(editingLine.note || '');
            // Rehidratación por RENGLÓN (helper puro y testeado: resuelve instancias y
            // las líneas viejas que solo traían groupId)
            setSelections(dish ? buildSelectionsFromModifiers(dish, editingLine.modifiers || []) : new Map());
            setRemovedInputIds(new Set((editingLine.removedIngredients || []).map((i) => i.inputId)));
        } else {
            resetData();
        }
    }, [modalDishInfo.modalOpen, editingLine, dish]);

    if (!dish) {
        return <Box />;
    }

    const dishGroups = (dish.modifierGroups || [])
        .map((dishGroup) => ({ config: dishGroup, group: modifierGroupsById.get(dishGroup.groupId) }))
        .filter((entry) => !!entry.group);

    // Nombre del renglón ligado por maxFromGroupId (acepta clave de instancia o, en
    // productos viejos, el groupId) — para el aviso "primero elige en ..."
    const getLinkedRowName = (linkedKey: string): string => {
        const row =
            (dish.modifierGroups || []).find((candidate) => getDishGroupKey(candidate) === linkedKey) ||
            (dish.modifierGroups || []).find((candidate) => candidate.groupId === linkedKey);
        return row ? getDishGroupLabel(row, modifierGroupsById.get(row.groupId)) : 'las piezas';
    };

    // Ojo: la clave es la del RENGLÓN (instanceId o groupId), no el groupId a secas —
    // el mismo modificador puede estar dos veces en el producto
    const getOptionQuantity = (groupKey: string, optionId: string): number =>
        selections.get(groupKey)?.get(optionId) || 0;

    const setOptionQuantity = (groupKey: string, optionId: string, nextQuantity: number) => {
        const updated = cloneSelections(selections);
        const options = updated.get(groupKey) || new Map<string, number>();
        if (nextQuantity <= 0) {
            options.delete(optionId);
        } else {
            options.set(optionId, nextQuantity);
        }
        updated.set(groupKey, options);
        setSelections(updated);
    };

    const onAddOption = (dishGroup: DishModifierGroup, optionId: string) => {
        const groupKey = getDishGroupKey(dishGroup);
        const currentTotal = getGroupSelectedTotal(selections, groupKey);
        const currentOption = getOptionQuantity(groupKey, optionId);
        // Máximo efectivo: fijo o ligado a las piezas elegidas en otro renglón
        const effectiveMax = getGroupEffectiveMax(dishGroup, selections, modifierGroupsById, dish.modifierGroups);
        if (dishGroup.maxFromGroupId && effectiveMax === 0) {
            const linkedName = getLinkedRowName(dishGroup.maxFromGroupId);
            store.dispatch(
                setAlert({
                    message: `Primero elige en "${linkedName}"`,
                    open: true,
                    severity: 'warning'
                })
            );
            return;
        }
        // Límite de selecciones del grupo (en v1 los extras ignoraban todo límite — aquí sí aplica)
        if (currentTotal >= effectiveMax) {
            // max = 1 y una sola opción por grupo: reemplazar es lo natural (comportamiento tipo radio)
            if (effectiveMax === 1) {
                const updated = cloneSelections(selections);
                updated.set(groupKey, new Map([[optionId, 1]]));
                setSelections(updated);
                return;
            }
            store.dispatch(
                setAlert({
                    message: dishGroup.maxFromGroupId
                        ? `Máximo ${effectiveMax} según las piezas elegidas`
                        : `Máximo ${effectiveMax} selección(es) en este grupo`,
                    open: true,
                    severity: 'warning'
                })
            );
            return;
        }
        // Máximo de opciones DISTINTAS ("hasta 2 sabores"): bloquea solo opciones NUEVAS,
        // subirle cantidad a una ya elegida sigue permitido
        const maxDistinct = dishGroup.maxDistinctOptions || 0;
        if (maxDistinct > 0 && currentOption === 0 && getGroupDistinctCount(selections, groupKey) >= maxDistinct) {
            store.dispatch(
                setAlert({
                    message: `Máximo ${maxDistinct} opciones distintas en este grupo — sube la cantidad de las ya elegidas`,
                    open: true,
                    severity: 'warning'
                })
            );
            return;
        }
        // Sin allowQuantity, cada opción se elige una sola vez
        if (!dishGroup.allowQuantity && currentOption >= 1) {
            store.dispatch(
                setAlert({
                    message: 'Este grupo no permite repetir la misma opción',
                    open: true,
                    severity: 'warning'
                })
            );
            return;
        }
        setOptionQuantity(groupKey, optionId, currentOption + 1);
    };

    const toggleRemovedIngredient = (inputId: string) => {
        const updated = new Set(removedInputIds);
        if (updated.has(inputId)) {
            updated.delete(inputId);
        } else {
            updated.add(inputId);
        }
        setRemovedInputIds(updated);
    };

    // Línea en vivo (misma función que construye la línea persistida)
    const removedIngredients: OrderLineRemovedIngredient[] = removableIngredients.filter((ingredient) =>
        removedInputIds.has(ingredient.inputId)
    );
    const previewLine = buildOrderLineV2({
        dish,
        selections,
        modifierGroupsById,
        removedIngredients,
        quantity,
        takeAway,
        note
    });
    const unitPrice = previewLine.unitPrice;
    const lineTotal = previewLine.cost;

    const onConfirm = () => {
        const validationError = validateDishSelections(dish, selections, modifierGroupsById);
        if (validationError) {
            store.dispatch(setAlert({ message: validationError, open: true, severity: 'warning' }));
            return;
        }
        onAddLine({ ...previewLine, id: editingLine?.id });
        handleClose();
    };

    const renderOption = (dishGroup: DishModifierGroup, option: ModifierGroupOption) => {
        const groupKey = getDishGroupKey(dishGroup);
        const optionQuantity = getOptionQuantity(groupKey, option.id);
        const unavailable = unavailableRecipeIds.has(option.recipeId);
        return (
            <Grid item xs={12} sm={6} key={option.id}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1,
                        borderRadius: 2,
                        borderColor: optionQuantity > 0 ? 'primary.main' : undefined,
                        bgcolor: optionQuantity > 0 ? '#F4F7FA' : undefined
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Stack flex={1} minWidth={0}>
                            {/* Nombre completo en hasta 2 líneas: los del catálogo legacy son largos
                                ("Orden de 12 pzas + 2 aderezos/salsas") y truncados no se distinguen */}
                            <Typography
                                variant="body2"
                                fontWeight={optionQuantity > 0 ? 700 : 400}
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    lineHeight: 1.25
                                }}
                            >
                                {option.name}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25} flexWrap="wrap">
                                {option.extraCost > 0 && (
                                    <Typography variant="caption" fontWeight={700} color="primary.main">
                                        +{currencyFormatter(option.extraCost)}
                                    </Typography>
                                )}
                                {unavailable && <Chip size="small" color="error" label="Agotado" />}
                            </Stack>
                        </Stack>
                        <Stack direction="row" alignItems="center">
                            <IconButton
                                size="small"
                                disabled={optionQuantity === 0}
                                onClick={() => setOptionQuantity(groupKey, option.id, optionQuantity - 1)}
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography width={24} textAlign="center" fontWeight={600}>
                                {optionQuantity}
                            </Typography>
                            <IconButton size="small" onClick={() => onAddOption(dishGroup, option.id)}>
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Paper>
            </Grid>
        );
    };

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 15, padding: 4 } }}
            open={modalDishInfo.modalOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={600}>
                        {dish.name}
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box
                            component="img"
                            src={dish.photoSrc || noImage}
                            alt={dish.name}
                            width="100%"
                            sx={{ aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 3 }}
                        />
                        <Typography variant="h6" fontWeight={700} mt={1}>
                            {currencyFormatter(dish.cost)}
                        </Typography>
                        {dish.description && (
                            <Typography variant="body2" color="text.secondary">
                                {dish.description}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={2}>
                            {/* Grupos de modificadores */}
                            {dishGroups.map(({ config, group }) => {
                                const groupKey = getDishGroupKey(config);
                                const total = getGroupSelectedTotal(selections, groupKey);
                                const minRequired = getGroupMinRequired(config);
                                const effectiveMax = getGroupEffectiveMax(
                                    config,
                                    selections,
                                    modifierGroupsById,
                                    dish.modifierGroups
                                );
                                const activeOptions = (group!.options || []).filter((option) => option.status);
                                return (
                                    <Stack key={groupKey} spacing={1}>
                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                            {/* La ETIQUETA del renglón ("Queso — Hamburguesa 2") cuando existe:
                                                el mismo modificador puede repetirse en el producto */}
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {getDishGroupLabel(config, group)}
                                            </Typography>
                                            {/* SIEMPRE se muestra el estado del grupo: sin esto, un grupo
                                                opcional con máximo (el caso más común del catálogo migrado)
                                                limitaba en silencio y parecía un error */}
                                            {minRequired > 0 ? (
                                                <Chip
                                                    size="small"
                                                    color={total >= minRequired ? 'success' : 'warning'}
                                                    label={
                                                        total >= minRequired
                                                            ? 'Listo'
                                                            : minRequired === effectiveMax
                                                            ? `Obligatorio · elige ${minRequired}`
                                                            : `Obligatorio · mín. ${minRequired}`
                                                    }
                                                />
                                            ) : (
                                                <Chip size="small" variant="outlined" label="Opcional" />
                                            )}
                                            <Typography variant="caption" color="text.secondary">
                                                {config.maxFromGroupId && effectiveMax === 0
                                                    ? 'elige primero las piezas'
                                                    : `${total}/${effectiveMax} seleccionado${
                                                          effectiveMax === 1 ? '' : 's'
                                                      }`}
                                                {config.maxFromGroupId ? ' · 1 por pieza' : ''}
                                                {(config.maxDistinctOptions || 0) > 0
                                                    ? ` · hasta ${config.maxDistinctOptions} distintas`
                                                    : ''}
                                                {config.allowQuantity ? ' · permite repetir' : ''}
                                            </Typography>
                                        </Stack>
                                        <Grid container spacing={1}>
                                            {activeOptions.map((option) => renderOption(config, option))}
                                        </Grid>
                                    </Stack>
                                );
                            })}

                            {/* Ingredientes que se pueden quitar */}
                            {removableIngredients.length > 0 && (
                                <Stack spacing={0.5}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Ingredientes
                                    </Typography>
                                    <Grid container>
                                        {removableIngredients.map((ingredient) => (
                                            <Grid item xs={12} sm={6} key={ingredient.inputId}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!removedInputIds.has(ingredient.inputId)}
                                                            onChange={() => toggleRemovedIngredient(ingredient.inputId)}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                textDecoration: removedInputIds.has(ingredient.inputId)
                                                                    ? 'line-through'
                                                                    : 'none'
                                                            }}
                                                        >
                                                            {ingredient.name}
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Stack>
                            )}

                            <Divider />
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1}>
                                <Stack direction="row" alignItems="center">
                                    <IconButton onClick={() => setQuantity(quantity > 1 ? quantity - 1 : 1)}>
                                        <RemoveIcon />
                                    </IconButton>
                                    <Typography width={32} textAlign="center" fontWeight={700}>
                                        {quantity}
                                    </Typography>
                                    <IconButton onClick={() => setQuantity(quantity + 1)}>
                                        <AddIcon />
                                    </IconButton>
                                </Stack>
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={takeAway} onChange={(e) => setTakeAway(e.target.checked)} />
                                    }
                                    label="Para llevar"
                                />
                            </Stack>
                            <TextField
                                fullWidth
                                label="Nota"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Ej. sin picante, bien cocido"
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Stack>
                        <Typography variant="caption" color="text.secondary">
                            {quantity} × {currencyFormatter(unitPrice)}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                            {currencyFormatter(lineTotal)}
                        </Typography>
                    </Stack>
                    <Button sx={{ minWidth: 220 }} color="primary" variant="contained" onClick={onConfirm}>
                        {editingLine ? 'Guardar cambios' : 'Agregar'}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
